// @ts-nocheck

const FUNDRAISING_API_BASE = 'https://sarris-fundraising-dashboard.vercel.app/apps/fundraising/api';

class FundraisingValidation {
  constructor() {
    this.modal = document.getElementById('fundraising-validation-modal');
    this.form = document.querySelector('[data-fundraising-form]');
    this.idInput = document.querySelector('[data-fundraising-id-input]');
    this.submitButton = document.querySelector('[data-submit-button]');
    this.buttonText = document.querySelector('[data-button-text]');
    this.buttonLoader = document.querySelector('[data-button-loader]');
    this.errorMessage = document.querySelector('[data-error-message]');
    this.changeIdButton = document.querySelector('[data-change-id-button]');
    this.removeIdButton = document.querySelector('[data-remove-id-button]');
    this.closeButtons = document.querySelectorAll('[data-modal-close]');
    this.changeIdWrapper = document.querySelector('[data-fundraising-change-wrapper]');

    this.STORAGE_KEY = 'active_fundraiser_code';
    this._lockedScrollY = undefined;
    this._checkoutListenerAttached = false;

    this.init();
  }

  async init() {
    const urlParamCaptured = await this.checkUrlParameters();
    const storedData = this.getStoredFundraiserCode();
    const autoShow = this.modal?.dataset.autoShow === 'true';

    if (storedData) {
      this.updateChangeIdDisplay();
      this.syncToCartAttributes();
    } else if (!urlParamCaptured && autoShow) {
      this.showModal();
    }

    this.attachEventListeners();
    this.toggleCheckoutButton();
  }

  async checkUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const groupId = urlParams.get('group_id');
    const sellerId = urlParams.get('seller_id');

    if (groupId && this.validateIdFormat(groupId)) {
      await this.autoValidateFromUrl(groupId, 'group');
      this.stripQueryParam('group_id');
      this.redirectToFundraisingHome();
      return true;
    } else if (sellerId && this.validateIdFormat(sellerId)) {
      await this.autoValidateFromUrl(sellerId, 'seller');
      this.stripQueryParam('seller_id');
      this.redirectToFundraisingHome();
      return true;
    }

    return false;
  }

  // Group ID: XX-XXXX or XX-XXXXX (e.g. 10-0511 or legacy 10-42661) | Seller ID: numeric digits
  validateIdFormat(id) {
    if (!id || typeof id !== 'string') return false;
    const trimmed = id.trim();
    return /^\d{2}-\d{4,5}$/.test(trimmed) || /^\d+$/.test(trimmed);
  }

  stripQueryParam(paramName) {
    const url = new URL(window.location);
    url.searchParams.delete(paramName);
    window.history.replaceState({}, document.title, url.toString());
  }

  redirectToFundraisingHome() {
    const target = '/pages/fundraising-home';
    if (window.location.pathname === target) return;
    window.location.assign(target);
  }

  async autoValidateFromUrl(id, type) {
    try {
      const result = await this.validateId(id);
      if (result.valid) {
        this.storeFundraiserCode(id, type, result.group_id || null, result.name || null, result.group_name || null);
        this.updateCustomerMetafield(id, type, '', '');
        this.syncToCartAttributes();
        this.hideModal();
        this.toggleCheckoutButton();
        this.updateChangeIdDisplay();
        if (this.idInput) this.idInput.value = id;
        console.log(`[Fundraising] Auto-validated from URL: ${type} ${id}`);
      } else {
        console.warn(`[Fundraising] URL param rejected: ${result.message}`);
      }
    } catch (error) {
      console.error('[Fundraising] Auto-validation failed:', error);
    }
  }

  attachEventListeners() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideModal();
      });
    });

    if (this.changeIdButton) {
      this.changeIdButton.addEventListener('click', () => this.handleChangeId());
    }

    if (this.removeIdButton) {
      this.removeIdButton.addEventListener('click', () => this.handleRemoveId());
    }

    if (this.idInput) {
      this.idInput.addEventListener('input', () => this.clearError());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('fundraising-modal--active')) {
        this.hideModal();
      }
    });

    // Block cart form submission when no fundraiser code is set
    document.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (form?.id === 'cart-form' && !this.getStoredFundraiserCode()) {
          e.preventDefault();
          e.stopPropagation();
          this.showModal();
        }
      },
      true,
    );

    // Re-apply checkout button state after cart re-renders (double rAF to run after morphdom)
    document.addEventListener('cart:update', () => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.toggleCheckoutButton()));
    });
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    const fundraisingId = this.idInput?.value.trim() || '';
    const sellerFirstName = document.getElementById('seller-first-name')?.value.trim() || '';
    const sellerLastName = document.getElementById('seller-last-name')?.value.trim() || '';

    if (!fundraisingId) {
      this.showError('Please enter a Group or Seller ID.');
      if (this.idInput) this.idInput.classList.add('error');
      return;
    }

    if (!this.validateIdFormat(fundraisingId)) {
      this.showError('Enter a valid Group ID (e.g. 10-0511) or numeric Seller ID');
      return;
    }

    this.setLoadingState(true);
    this.clearError();

    try {
      const validationResult = await this.validateId(fundraisingId);

      if (validationResult.valid !== false) {
        const idType = validationResult.type || this.determineIdType(fundraisingId);

        const typedName = [sellerFirstName, sellerLastName].filter(Boolean).join(' ') || null;
        const displayName = validationResult.name || typedName;
        this.storeFundraiserCode(
          fundraisingId,
          idType,
          validationResult.group_id || null,
          displayName,
          validationResult.group_name || null,
        );
        this.updateCustomerMetafield(fundraisingId, idType, sellerFirstName, sellerLastName);
        this.syncToCartAttributes();
        this.hideModal();
        this.toggleCheckoutButton();
        this.updateChangeIdDisplay();

        console.log(`[Fundraising] Form validated: ${idType} ${fundraisingId}`);
      } else {
        this.showError(validationResult.message || 'Invalid ID. Please check your code and try again.');
      }
    } catch (error) {
      console.error('[Fundraising] Validation error:', error);
      this.showError('An error occurred. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }

  async validateId(id) {
    const type = this.determineIdType(id);

    if (type === 'group') {
      try {
        const response = await fetch(`${FUNDRAISING_API_BASE}/group-info?group_id=${encodeURIComponent(id.trim())}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.group_id) {
            if (data.has_active_campaign === false) {
              return { valid: false, message: 'This group does not have an active campaign.' };
            }
            return { valid: true, type: 'group', id: id.trim(), name: data.group_name || data.org_name || null };
          }
        }
        return { valid: false, message: 'Group ID not found. Please check your code and try again.' };
      } catch (error) {
        console.warn('[Fundraising] Group validation API error:', error);
        return { valid: false, message: 'Could not verify Group ID. Please check your connection and try again.' };
      }
    }

    try {
      const response = await fetch(
        `${FUNDRAISING_API_BASE}/seller-validate?seller_id=${encodeURIComponent(id.trim())}`,
      );
      if (response.ok) {
        const data = await response.json();
        if (data && data.valid) {
          const sellerName =
            data.seller_name ||
            (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name || null);
          return {
            valid: true,
            type: 'seller',
            id: id.trim(),
            group_id: data.group_id || null,
            group_name: data.group_name || null,
            name: sellerName,
          };
        }
      }
      return { valid: false, message: 'Seller ID not found. Please check your code and try again.' };
    } catch (error) {
      console.warn('[Fundraising] Seller validation API error:', error);
      return { valid: false, message: 'Could not verify Seller ID. Please check your connection and try again.' };
    }
  }

  async fetchCurrentCampaignGroupId() {
    try {
      const response = await fetch(`${FUNDRAISING_API_BASE}/current-campaign`);
      if (!response.ok) return null;
      const data = await response.json();
      return data?.group_id || null;
    } catch {
      return null;
    }
  }

  // Returns 'group' for XX-XXXXX format, 'seller' for numeric.
  // When the modal is open, respects the toggle selection so the user's explicit choice is honoured.
  determineIdType(id) {
    const modal = document.getElementById('fundraising-validation-modal');
    const modalIsVisible = modal && !modal.classList.contains('fundraising-modal--hidden');
    if (modalIsVisible) {
      const activeToggle = document.querySelector('.fundraising-modal__toggle-btn--active');
      if (activeToggle) {
        return activeToggle.getAttribute('data-id-type') === 'seller' ? 'seller' : 'group';
      }
    }
    return /^\d{2}-\d{4,5}$/.test(id.trim()) ? 'group' : 'seller';
  }

  getStoredFundraiserCode() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      const data = JSON.parse(stored);
      return data && data.id && data.type ? data : null;
    } catch (error) {
      console.warn('[Fundraising] Error reading stored code:', error);
      return null;
    }
  }

  storeFundraiserCode(id, type, group_id = null, name = null, group_name = null) {
    try {
      const data = { id: id.trim(), type: type || this.determineIdType(id) };
      if (group_id) data.group_id = group_id;
      if (name) data.name = name;
      if (group_name) data.group_name = group_name;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      console.log('[Fundraising] Stored code:', data);
    } catch (error) {
      console.error('[Fundraising] Error storing code:', error);
    }
  }

  removeFundraiserCode() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('[Fundraising] Error removing code:', error);
    }
  }

  async updateCustomerMetafield(id, type, firstName, lastName) {
    try {
      const response = await fetch('/apps/fundraising/update-customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: window.ShopifyCustomer?.id || null,
          fundraising_id: id,
          fundraising_type: type,
          seller_first_name: firstName,
          seller_last_name: lastName,
        }),
      });
      if (!response.ok) console.warn('Failed to update customer metafield');
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.error('Customer metafield update error:', error);
      return null;
    }
  }

  handleChangeId() {
    this.removeFundraiserCode();
    if (this.idInput) this.idInput.value = '';
    this.showModal();
    if (this.idInput) setTimeout(() => this.idInput.focus(), 100);
  }

  handleRemoveId() {
    if (confirm('Are you sure you want to remove this fundraiser code?')) {
      this.removeFundraiserCode();
      this.removeFromCartAttributes();
      this.hideModal();
      this.updateChangeIdDisplay();
      this.toggleCheckoutButton();
    }
  }

  clearValidation() {
    this.removeFundraiserCode();
  }

  showModal() {
    if (this.modal) {
      this.closeCartDrawer();
      this.modal.classList.add('fundraising-modal--active');
      this.modal.classList.remove('fundraising-modal--hidden');
      this._lockedScrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._lockedScrollY}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'none';
    }
  }

  closeCartDrawer() {
    const closeBtn = document.querySelector('cart-drawer-component .cart-drawer__close-button');
    if (closeBtn) closeBtn.click();
  }

  hideModal() {
    if (this.modal) {
      this.modal.classList.remove('fundraising-modal--active');
      this.modal.classList.add('fundraising-modal--hidden');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
      if (this._lockedScrollY !== undefined) {
        window.scrollTo(0, this._lockedScrollY);
        this._lockedScrollY = undefined;
      }
    }
  }

  toggleCheckoutButton() {
    const isValidated = !!this.getStoredFundraiserCode();

    // Use a body class so the disabled state survives DOM re-renders from morphdom
    document.body.classList.toggle('fundraising-not-validated', !isValidated);

    // Also set the disabled attribute on any currently present buttons
    document
      .querySelectorAll('[name="checkout"], .cart__checkout-button, [href="/checkout"], .btn--checkout')
      .forEach((button) => {
        button.disabled = !isValidated;
      });

    if (!this._checkoutListenerAttached) {
      this._checkoutListenerAttached = true;
      document.addEventListener(
        'click',
        (e) => {
          const btn = e.target?.closest(
            '[name="checkout"], .cart__checkout-button, [href="/checkout"], .btn--checkout',
          );
          if (!btn) return;
          if (!this.getStoredFundraiserCode()) {
            e.preventDefault();
            e.stopPropagation();
            this.showModal();
          }
        },
        true,
      );
    }
  }

  // Format the "Supporting: X" label.
  // Seller: "Seller Name (Group Name)" — Group: "Group Name"
  formatSupportingLabel(storedData) {
    if (!storedData) return '';
    const name = storedData.name || storedData.id;
    if (storedData.type === 'seller') {
      return storedData.group_name ? `${name} (${storedData.group_name})` : name;
    }
    return name;
  }

  updateChangeIdDisplay() {
    const currentIdElement = document.querySelector('[data-current-id]');
    const changeWrapper = document.querySelector('[data-fundraising-change-wrapper]');
    const storedData = this.getStoredFundraiserCode();

    if (storedData) {
      if (changeWrapper) changeWrapper.style.display = 'flex';
      if (currentIdElement) currentIdElement.textContent = this.formatSupportingLabel(storedData);
      // Backfill in the background if name is missing, or if a seller is missing its group_name
      if (!storedData.name || (storedData.type === 'seller' && !storedData.group_name)) {
        this.fetchAndCacheDisplayName(storedData, currentIdElement);
      }
    } else {
      if (changeWrapper) changeWrapper.style.display = 'none';
    }
  }

  async fetchAndCacheDisplayName(storedData, currentIdElement) {
    try {
      let name = null;
      if (storedData.type === 'seller') {
        const res = await fetch(
          `${FUNDRAISING_API_BASE}/seller-validate?seller_id=${encodeURIComponent(storedData.id)}`,
        );
        if (res.ok) {
          const data = await res.json();
          if (data && data.valid) {
            name =
              data.seller_name ||
              (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name || null);
            const groupName = data.group_name || null;
            this.storeFundraiserCode(
              storedData.id,
              storedData.type,
              data.group_id || storedData.group_id || null,
              name,
              groupName,
            );
            if (currentIdElement)
              currentIdElement.textContent = this.formatSupportingLabel({ ...storedData, name, group_name: groupName });
            return;
          }
        }
      } else if (storedData.type === 'group') {
        const res = await fetch(`${FUNDRAISING_API_BASE}/group-info?group_id=${encodeURIComponent(storedData.id)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.group_id) {
            name = data.group_name || data.org_name || null;
          }
        }
      }
      if (name) {
        this.storeFundraiserCode(
          storedData.id,
          storedData.type,
          storedData.group_id || null,
          name,
          storedData.group_name || null,
        );
        if (currentIdElement) currentIdElement.textContent = this.formatSupportingLabel({ ...storedData, name });
      }
    } catch (e) {
      // silently fail — ID is already showing as fallback
    }
  }

  showError(message) {
    if (this.errorMessage) this.errorMessage.textContent = message;
    if (this.idInput) this.idInput.classList.add('error');
  }

  clearError() {
    if (this.errorMessage) this.errorMessage.textContent = '';
    if (this.idInput) this.idInput.classList.remove('error');
  }

  setLoadingState(loading) {
    if (this.submitButton) this.submitButton.disabled = loading;
    if (this.buttonText) this.buttonText.style.display = loading ? 'none' : 'inline';
    if (this.buttonLoader) this.buttonLoader.style.display = loading ? 'inline-block' : 'none';
  }

  async syncToCartAttributes() {
    try {
      const storedData = this.getStoredFundraiserCode();
      if (!storedData) return;

      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) throw new Error('Failed to fetch cart');

      const cart = await cartResponse.json();
      const attributes = { ...cart.attributes };

      delete attributes['fundraising.group_id'];
      delete attributes['fundraising.seller_id'];

      const typeKey = storedData.type === 'group' ? 'fundraising.group_id' : 'fundraising.seller_id';
      attributes[typeKey] = storedData.id;
      attributes['fundraising.id'] = storedData.id;
      attributes['fundraising.type'] = storedData.type;

      // For sellers, also attach the group they belong to
      if (storedData.type === 'seller' && storedData.group_id) {
        attributes['fundraising.group_id'] = storedData.group_id;
      }

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      if (updateResponse.ok) {
        console.log('[Fundraising] Synced to cart attributes:', storedData);
        document.dispatchEvent(new CustomEvent('fundraising:synced', { detail: storedData }));
      }
    } catch (error) {
      console.warn('[Fundraising] Failed to sync cart attributes:', error);
    }
  }

  async removeFromCartAttributes() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) throw new Error('Failed to fetch cart');

      const cart = await cartResponse.json();
      const attributes = { ...cart.attributes };

      delete attributes['fundraising.group_id'];
      delete attributes['fundraising.seller_id'];

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (error) {
      console.warn('[Fundraising] Failed to remove cart attributes:', error);
    }
  }
}

function _initFundraisingValidation() {
  if (window.FundraisingValidationInitialized) return;
  window.fundraisingValidator = new FundraisingValidation();
  window.FundraisingValidationInitialized = true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _initFundraisingValidation);
} else {
  _initFundraisingValidation();
}

window.FundraisingValidation = FundraisingValidation;
